/**
 * src/components/legal/CookiesBanner.tsx
 * =============================================================================
 * Aviso informativo de cookies para la landing pública.
 *
 * Por qué un aviso y no un consent management:
 *  Ploter actualmente sólo usa cookies y almacenamiento estrictamente
 *  necesarios (auth, tema, idioma). Conforme al art. 22.2 LSSI no requieren
 *  consentimiento. Mantenemos un aviso de transparencia que el usuario
 *  cierra una vez. Si se incorporasen cookies analíticas o de terceros,
 *  este componente debe sustituirse por un consent banner real con
 *  granularidad por categoría.
 *
 * Persistencia del cierre: localStorage `ploter:cookies-acknowledged`.
 * No se renderiza nada si ya fue aceptado.
 *
 * Posición: fixed bottom inset, max-w-md a la izquierda. No bloquea el
 * scroll de la landing.
 * =============================================================================
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { X } from 'lucide-react'

interface Props {
  /**
   * Callback opcional para "Más información". Si se pasa, el link abre lo
   * que el padre quiera (típicamente un LegalModal sin navegar). Si no se
   * pasa, no se muestra el link — para evitar romper la inmersión en
   * pantallas donde no hay LegalModal montado.
   */
  onMoreInfo?: () => void
}

const STORAGE_KEY = 'ploter:cookies-acknowledged'

/** Lee el flag de localStorage en init lazy. Vite es client-only, así que
 *  no hay riesgo de SSR mismatch. Esto evita el flash que tenía la versión
 *  con useEffect (visible=false → mount → setVisible(true)) y la regla
 *  react-hooks/set-state-in-effect de React 19. */
function readInitialVisible(): boolean {
  try {
    return !localStorage.getItem(STORAGE_KEY)
  } catch {
    // entornos sin localStorage (privado/embebidos): no mostrar.
    return false
  }
}

export function CookiesBanner({ onMoreInfo }: Props = {}) {
  const { t } = useTranslation()
  const [visible, setVisible] = useState<boolean>(readInitialVisible)
  const caja = useRef<HTMLDivElement>(null)

  /**
   * Publica la altura del banner en una variable CSS para que lo que vive
   * pegado abajo se aparte.
   *
   * En un móvil el banner ocupa todo el ancho, y antes tapaba el pie de la
   * landing —con los enlaces de Términos, Privacidad y Cookies— y el botón de
   * ayuda. Es decir: tapaba justamente los enlaces legales a los que el propio
   * banner te está mandando. Detectado midiendo solapes con un navegador a
   * 390 px el 2026-08-22.
   *
   * Se hace con una variable CSS y no pasando estado entre componentes porque
   * el banner es global y no conoce a nadie: cualquier pantalla que ponga algo
   * abajo puede leer `--espacio-banner` sin que haya que acordarse de
   * conectarlos. Lo que no se conecta a mano no se puede olvidar.
   */
  useEffect(() => {
    const raiz = document.documentElement
    if (!visible) {
      raiz.style.setProperty('--espacio-banner', '0px')
      return undefined
    }
    const medir = () => {
      const alto = caja.current?.getBoundingClientRect().height ?? 0
      raiz.style.setProperty('--espacio-banner', `${Math.round(alto)}px`)
    }
    medir()
    window.addEventListener('resize', medir)
    return () => {
      window.removeEventListener('resize', medir)
      raiz.style.setProperty('--espacio-banner', '0px')
    }
  }, [visible])

  if (!visible) return null

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1')
    } catch {
      // ignorar fallo de escritura
    }
    setVisible(false)
  }

  return (
    <div
      ref={caja}
      // Diálogo NO modal a propósito: es un aviso informativo que convive con
      // la landing. Sin aria-modal y sin trampa de foco — atrapar el teclado
      // en un aviso de cookies bloquearía la página entera hasta cerrarlo.
      role="dialog"
      aria-label={t('common:cookiesBanner.message')}
      aria-live="polite"
      className="fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-md z-50 rounded-lg border border-border bg-background shadow-lg"
    >
      <div className="p-4 space-y-3">
        <p className="text-xs leading-relaxed text-muted-foreground">
          {t('common:cookiesBanner.message')}
          {onMoreInfo && (
            <>
              {' '}
              <button
                type="button"
                onClick={onMoreInfo}
                className="inline-flex items-center h-6 text-foreground underline underline-offset-2 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 rounded"
              >
                {t('common:cookiesBanner.moreInfo')}
              </button>
            </>
          )}
        </p>
        <div className="flex justify-end">
          <Button size="sm" onClick={dismiss}>
            {t('common:cookiesBanner.accept')}
          </Button>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t('common:cookiesBanner.close')}
        className="absolute top-1.5 right-1.5 h-8 w-8 flex items-center justify-center p-1 text-muted-foreground hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" strokeWidth={1.5} />
      </button>
    </div>
  )
}
